import { profileImageUrl } from "@web/utils/upload-url";
import { useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  ChevronDown,
  ChevronRight,
  Minus,
  Plus,
  RotateCcw,
} from "lucide-react";

export interface StructurePerson {
  id: number;
  name: string;
  email: string;
  profileImageUrl?: string | null;
  managerId?: number | null;
  managerName?: string | null;
  assignmentId?: number | null;
}
export function SalesStructure({
  directors = [],
  managers,
  consultants,
  onManage,
  onAssign,
  onTransfer,
  onUnassign,
  readOnly = false,
  showUnassigned = true,
  onViewLeads,
}: {
  directors?: StructurePerson[];
  managers: StructurePerson[];
  consultants: StructurePerson[];
  onManage?: (manager: StructurePerson) => void;
  onAssign?: (person: StructurePerson) => void;
  onTransfer?: (person: StructurePerson) => void;
  onUnassign?: (person: StructurePerson) => void;
  readOnly?: boolean;
  showUnassigned?: boolean;
  onViewLeads?: (person: StructurePerson) => void;
}) {
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const [zoom, setZoom] = useState(1);
  const unassigned = consultants.filter((person) => !person.managerId);
  const managerIds = new Set(managers.map((manager) => manager.id));
  const needsReview = consultants.filter(
    (person) => person.managerId && !managerIds.has(person.managerId),
  );
  const assignedCount =
    consultants.length - unassigned.length - needsReview.length;
  const initials = (name: string) =>
    name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase();
  const line = "1px solid";
  function personCard(
    person: StructurePerson,
    manager: boolean,
    children = 0,
    director = false,
  ) {
    return (
      <Paper
        variant="outlined"
        sx={(theme) => ({
          width: 216,
          flexShrink: 0,
          textAlign: "center",
          p: 2,
          borderRadius: 2.5,
          borderColor: manager
            ? alpha(theme.palette.primary.main, 0.45)
            : "divider",
          bgcolor: manager
            ? alpha(theme.palette.primary.main, 0.06)
            : "background.paper",
          position: "relative",
        })}
      >
        <Avatar
          src={profileImageUrl(person.profileImageUrl)}
          alt={person.name}
          sx={{
            width: 48,
            height: 48,
            mx: "auto",
            mb: 1,
            bgcolor: manager ? "primary.main" : "success.light",
            color: manager ? "primary.contrastText" : "success.contrastText",
          }}
        >
          {initials(person.name)}
        </Avatar>
        <Typography fontWeight={700} sx={{ overflowWrap: "anywhere" }}>
          {person.name}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", overflowWrap: "anywhere", mb: 1 }}
        >
          {person.email}
        </Typography>
        <Chip
          size="small"
          label={
            director
              ? "Sales Director"
              : manager
                ? "Sales Manager"
                : "Sales Consultant"
          }
          color={manager ? "primary" : "default"}
          variant="outlined"
        />
        {director ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1 }}
          >
            All sales teams
          </Typography>
        ) : manager ? (
          <Stack alignItems="center" sx={{ mt: 1 }}>
            <Button
              size="small"
              aria-expanded={!collapsed.has(person.id)}
              aria-label={`${collapsed.has(person.id) ? "Expand" : "Collapse"} ${person.name}'s team`}
              startIcon={
                collapsed.has(person.id) ? (
                  <ChevronRight size={15} />
                ) : (
                  <ChevronDown size={15} />
                )
              }
              onClick={() =>
                setCollapsed((previous) => {
                  const next = new Set(previous);
                  if (next.has(person.id)) next.delete(person.id);
                  else next.add(person.id);
                  return next;
                })
              }
            >
              {children} {children === 1 ? "consultant" : "consultants"}
            </Button>
            {readOnly && onViewLeads && (
              <Button size="small" onClick={() => onViewLeads(person)}>
                View manager leads
              </Button>
            )}
            {!readOnly && (
              <Button size="small" onClick={() => onManage?.(person)}>
                Manage team
              </Button>
            )}
          </Stack>
        ) : readOnly ? (
          onViewLeads ? (
            <Button
              size="small"
              sx={{ mt: 1 }}
              onClick={() => onViewLeads(person)}
            >
              View leads
            </Button>
          ) : null
        ) : (
          <Stack direction="row" justifyContent="center" sx={{ mt: 1 }}>
            {person.assignmentId ? (
              <>
                <Button size="small" onClick={() => onTransfer?.(person)}>
                  Transfer
                </Button>
                <Button
                  size="small"
                  color="warning"
                  onClick={() => onUnassign?.(person)}
                >
                  Unassign
                </Button>
              </>
            ) : (
              <Button size="small" onClick={() => onAssign?.(person)}>
                Assign manager
              </Button>
            )}
          </Stack>
        )}
      </Paper>
    );
  }
  return (
    <Stack spacing={3} sx={{ pt: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        gap={1}
      >
        <Box>
          <Typography variant="h6">Sales reporting structure</Typography>
          <Typography variant="body2" color="text.secondary">
            {directors.length > 0 &&
              `${directors.length} ${directors.length === 1 ? "director" : "directors"} · `}
            {managers.length} {managers.length === 1 ? "manager" : "managers"} ·{" "}
            {assignedCount} assigned consultants
            {needsReview.length > 0 &&
              ` · ${needsReview.length} needing review`}
            {showUnassigned && ` · ${unassigned.length} unassigned`}
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Button size="small" onClick={() => setCollapsed(new Set())}>
            Expand all
          </Button>
          <Tooltip title="Zoom out">
            <span>
              <IconButton
                aria-label="Zoom out"
                disabled={zoom <= 0.5}
                onClick={() => setZoom((value) => Math.max(0.5, value - 0.1))}
              >
                <Minus size={18} />
              </IconButton>
            </span>
          </Tooltip>
          <Typography
            variant="caption"
            sx={{ minWidth: 36, textAlign: "center" }}
          >
            {Math.round(zoom * 100)}%
          </Typography>
          <Tooltip title="Zoom in">
            <span>
              <IconButton
                aria-label="Zoom in"
                disabled={zoom >= 1.4}
                onClick={() => setZoom((value) => Math.min(1.4, value + 0.1))}
              >
                <Plus size={18} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Reset zoom">
            <IconButton aria-label="Reset zoom" onClick={() => setZoom(1)}>
              <RotateCcw size={16} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      <Box
        role="region"
        aria-label="Sales reporting organization chart"
        tabIndex={0}
        sx={(theme) => ({
          overflow: "auto",
          maxHeight: "65vh",
          minHeight: 300,
          p: 3,
          border: line,
          borderColor: "divider",
          borderRadius: 2,
          bgcolor: "background.default",
          backgroundImage: `radial-gradient(${alpha(theme.palette.text.primary, 0.12)} 1px, transparent 1px)`,
          backgroundSize: "20px 20px",
        })}
      >
        <Box sx={{ zoom, width: "max-content", minWidth: "100%" }}>
          {directors.length > 0 && (
            <Stack alignItems="center">
              <Box
                component="ul"
                aria-label="Sales Directors"
                sx={{ display: "flex", gap: 3, listStyle: "none", p: 0, m: 0 }}
              >
                {directors.map((director) => (
                  <Box component="li" key={director.id}>
                    {personCard(director, true, 0, true)}
                  </Box>
                ))}
              </Box>
              <Box
                aria-hidden
                sx={{
                  height: 32,
                  borderLeft: line,
                  borderColor: "text.disabled",
                }}
              />
            </Stack>
          )}
          <Box
            component="ul"
            aria-label="Sales Managers"
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
              gap: 6,
              p: 0,
              m: 0,
              listStyle: "none",
            }}
          >
            {managers.map((manager, managerIndex) => {
              const team = consultants.filter(
                (person) => person.managerId === manager.id,
              );
              const expanded = !collapsed.has(manager.id);
              return (
                <Box
                  component="li"
                  key={manager.id}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    minWidth: 240,
                    position: "relative",
                    ...(directors.length
                      ? {
                          pt: "32px",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: "50%",
                            height: 32,
                            borderLeft: line,
                            borderColor: "text.disabled",
                          },
                          "&::after": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: managerIndex === 0 ? "50%" : -24,
                            right:
                              managerIndex === managers.length - 1
                                ? "50%"
                                : -24,
                            borderTop: line,
                            borderColor: "text.disabled",
                          },
                        }
                      : {}),
                  }}
                >
                  {personCard(manager, true, team.length)}
                  {expanded &&
                    (team.length ? (
                      <>
                        <Box
                          aria-hidden
                          sx={{
                            height: 28,
                            borderLeft: line,
                            borderColor: "text.disabled",
                          }}
                        />
                        <Box
                          component="ul"
                          aria-label={`${manager.name}'s consultants`}
                          sx={{
                            display: "flex",
                            listStyle: "none",
                            m: 0,
                            p: 0,
                          }}
                        >
                          {team.map((person, index) => (
                            <Box
                              component="li"
                              key={person.id}
                              sx={{
                                position: "relative",
                                px: 1.5,
                                pt: "28px",
                                "&::before": {
                                  content: '""',
                                  position: "absolute",
                                  top: 0,
                                  left:
                                    team.length === 1
                                      ? "50%"
                                      : index === 0
                                        ? "50%"
                                        : 0,
                                  right:
                                    team.length === 1
                                      ? "50%"
                                      : index === team.length - 1
                                        ? "50%"
                                        : 0,
                                  borderTop: line,
                                  borderColor: "text.disabled",
                                },
                                "&::after": {
                                  content: '""',
                                  position: "absolute",
                                  top: 0,
                                  height: 28,
                                  left: "50%",
                                  borderLeft: line,
                                  borderColor: "text.disabled",
                                },
                              }}
                            >
                              {personCard(person, false)}
                            </Box>
                          ))}
                        </Box>
                      </>
                    ) : (
                      <Typography
                        color="text.secondary"
                        variant="body2"
                        sx={{ pt: 3 }}
                      >
                        No consultants assigned
                      </Typography>
                    ))}
                </Box>
              );
            })}
          </Box>
          {!managers.length && (
            <Typography color="text.secondary" sx={{ p: 3 }}>
              No Sales Managers found.
            </Typography>
          )}
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary">
        Scroll or swipe to explore larger teams. Expand or collapse a manager to
        focus the structure.
      </Typography>
      {needsReview.length > 0 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Assignments needing review ({needsReview.length})
          </Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            These saved assignments point to someone who is no longer in the
            Sales Manager list. Review them before transferring or unassigning.
            No assignment has been changed.
          </Alert>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {needsReview.map((person) => (
              <Box key={person.id}>
                {personCard(person, false)}
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ mt: 1, maxWidth: 216 }}
                >
                  Saved manager:{" "}
                  {person.managerName || `User #${person.managerId}`}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
      {showUnassigned && (
        <Box>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <Typography variant="h6">Unassigned consultants</Typography>
            <Chip size="small" label={unassigned.length} />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These consultants do not currently report to a Sales Manager.
          </Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {unassigned.map((person) => (
              <Box key={person.id}>{personCard(person, false)}</Box>
            ))}
          </Box>
          {!unassigned.length && (
            <Typography variant="body2" color="text.secondary">
              No consultants are unassigned.
            </Typography>
          )}
        </Box>
      )}
    </Stack>
  );
}
